import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './QuickTest.css';

interface Question {
  id: number;
  key: string;
  type: 'likert' | 'choice';
  options?: string[];
}

const QuickTest: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string | number>>({});
  const [showError, setShowError] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [testResults, setTestResults] = useState<any>(null);

  // Define questions
  const questions: Question[] = [
    { id: 1, key: 'q1', type: 'likert' },
    { id: 2, key: 'q2', type: 'likert' },
    { id: 3, key: 'q3', type: 'likert' },
    { id: 4, key: 'q4', type: 'likert' },
    { id: 5, key: 'q5', type: 'likert' },
    { id: 6, key: 'q6', type: 'likert' },
    { id: 7, key: 'q7', type: 'likert' },
    { id: 8, key: 'q8', type: 'likert' },
    { id: 9, key: 'q9', type: 'likert' },
    { 
      id: 10, 
      key: 'q10', 
      type: 'choice',
      options: ['milestone1', 'milestone2', 'milestone3', 'milestone4', 'milestone5']
    },
  ];

  // Handle answer selection
  const handleAnswer = (answer: string | number) => {
    setAnswers({
      ...answers,
      [questions[currentQuestionIndex].key]: answer
    });
    setShowError(false);
  };

  // Navigate to next question
  const handleNext = () => {
    if (!answers[questions[currentQuestionIndex].key]) {
      setShowError(true);
      return;
    }

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      handleSubmit();
    }
  };

  // Navigate to previous question
  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  // Submit all answers
  const handleSubmit = async () => {
    setProcessing(true);
    
    // Analyze personality based on answers
    const personalityAnalysis = analyzePersonality(answers);
    
    // Simulate API call delay for result processing
    setTimeout(() => {
      setTestResults(personalityAnalysis);
      setCompleted(true);
      setProcessing(false);
    }, 2000);
  };

  // Sample personality analysis function
  const analyzePersonality = (answers: Record<string, string | number>) => {
    // Calculate introversion/extroversion (q1, q4, q7)
    const sociabilityScore = typeof answers.q1 === 'number' ? Math.abs(6 - answers.q1) : 3;
    const riskScore = typeof answers.q4 === 'number' ? answers.q4 : 3;
    const independenceScore = typeof answers.q7 === 'number' ? answers.q7 : 3;
    
    const extroversionScore = (sociabilityScore + riskScore + (6 - independenceScore)) / 3;
    
    // Calculate thinking/feeling (q2, q6)
    const logicalScore = typeof answers.q2 === 'number' ? answers.q2 : 3;
    const creativeScore = typeof answers.q6 === 'number' ? answers.q6 : 3;
    
    const thinkingScore = (logicalScore + (6 - creativeScore)) / 2;
    
    // Calculate judging/perceiving (q3, q8)
    const planningScore = typeof answers.q3 === 'number' ? answers.q3 : 3;
    const organizationScore = typeof answers.q8 === 'number' ? answers.q8 : 3;
    
    const judgingScore = (planningScore + organizationScore) / 2;
    
    // Calculate sensing/intuition (q5)
    const practicalScore = typeof answers.q5 === 'number' ? answers.q5 : 3;
    
    // Calculate stability/growth (q9)
    const stabilityScore = typeof answers.q9 === 'number' ? answers.q9 : 3;
    
    // Estimate age range from q10
    let ageRange = '25-34';
    if (answers.q10 === 'milestone1') ageRange = '18-24';
    else if (answers.q10 === 'milestone2') ageRange = '25-34';
    else if (answers.q10 === 'milestone3') ageRange = '35-44';
    else if (answers.q10 === 'milestone4') ageRange = '45-54';
    else if (answers.q10 === 'milestone5') ageRange = '55+';
    
    // Determine personality type
    let personalityType = '';
    personalityType += extroversionScore >= 3 ? 'E' : 'I';
    personalityType += practicalScore >= 3 ? 'S' : 'N';
    personalityType += thinkingScore >= 3 ? 'T' : 'F';
    personalityType += judgingScore >= 3 ? 'J' : 'P';
    
    // Guess gender based on answers patterns
    // Note: This is a simplified approach and not scientifically accurate
    const femininityMarkers = [
      typeof answers.q6 === 'number' && answers.q6 > 3, // Higher interest in creative expression
      typeof answers.q8 === 'number' && answers.q8 > 3, // Higher organization preference
      typeof answers.q2 === 'number' && answers.q2 < 3, // Lower logical analysis preference
    ];
    
    const masculinityMarkers = [
      typeof answers.q2 === 'number' && answers.q2 > 3, // Higher logical analysis preference
      typeof answers.q4 === 'number' && answers.q4 > 3, // Higher risk-taking
      typeof answers.q6 === 'number' && answers.q6 < 3, // Lower interest in creative expression
    ];
    
    const femininityScore = femininityMarkers.filter(Boolean).length;
    const masculinityScore = masculinityMarkers.filter(Boolean).length;
    
    const likelyGender = femininityScore > masculinityScore ? 'female' : 'male';
    
    // Create personality profile with traits
    return {
      personalityType,
      traits: {
        extroversion: Math.round(extroversionScore * 20),
        intuition: Math.round((6 - practicalScore) * 20),
        thinking: Math.round(thinkingScore * 20),
        judging: Math.round(judgingScore * 20),
        stability: Math.round(stabilityScore * 20)
      },
      demographics: {
        likelyAgeRange: ageRange,
        likelyGender: likelyGender
      }
    };
  };

  const renderLikertQuestion = (questionKey: string) => {
    const options = [1, 2, 3, 4, 5];
    const labels = ['stronglyDisagree', 'disagree', 'neutral', 'agree', 'stronglyAgree'];
    
    return (
      <div className="likert-scale">
        {options.map((option, index) => (
          <div key={option} className="likert-option">
            <button
              className={`likert-button ${answers[questionKey] === option ? 'selected' : ''}`}
              onClick={() => handleAnswer(option)}
            >
              {option}
            </button>
            <span className="likert-label">{t(labels[index])}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderChoiceQuestion = (questionKey: string, options: string[]) => {
    return (
      <div className="choice-options">
        {options.map((option, index) => (
          <button
            key={option}
            className={`choice-button ${answers[questionKey] === option ? 'selected' : ''}`}
            onClick={() => handleAnswer(option)}
          >
            {t(option)}
          </button>
        ))}
      </div>
    );
  };

  const renderResults = () => {
    if (!testResults) return null;
    
    const { personalityType, traits, demographics } = testResults;
    
    // Personality type descriptions
    const typeDescriptions: Record<string, { title: string, description: string }> = {
      'ISTJ': {
        title: i18n.language === 'en' ? 'The Inspector' : 'Denetleyici',
        description: i18n.language === 'en' 
          ? 'Practical, detail-oriented, and reliable. You value tradition and loyalty.'
          : 'Pratik, detay odaklı ve güvenilir. Gelenek ve sadakate değer verirsiniz.'
      },
      'ISFJ': {
        title: i18n.language === 'en' ? 'The Protector' : 'Koruyucu',
        description: i18n.language === 'en'
          ? 'Caring, dependable, and organized. You are committed to helping others.'
          : 'İlgili, güvenilir ve düzenlisiniz. Başkalarına yardım etmeye kararlısınız.'
      },
      'INFJ': {
        title: i18n.language === 'en' ? 'The Counselor' : 'Danışman',
        description: i18n.language === 'en'
          ? 'Insightful, principled, and creative. You seek meaning and connection in life.'
          : 'Anlayışlı, prensipli ve yaratıcı. Hayatta anlam ve bağlantı ararsınız.'
      },
      'INTJ': {
        title: i18n.language === 'en' ? 'The Architect' : 'Mimar',
        description: i18n.language === 'en'
          ? 'Strategic, innovative, and independent. You have high standards and drive for improvement.'
          : 'Stratejik, yenilikçi ve bağımsız. Yüksek standartlara ve ilerleme dürtüsüne sahipsiniz.'
      },
      'ISTP': {
        title: i18n.language === 'en' ? 'The Craftsman' : 'Zanaatkar',
        description: i18n.language === 'en'
          ? 'Practical, adaptable, and analytical. You enjoy understanding how things work.'
          : 'Pratik, uyumlu ve analitik. Şeylerin nasıl çalıştığını anlamaktan keyif alırsınız.'
      },
      'ISFP': {
        title: i18n.language === 'en' ? 'The Composer' : 'Besteci',
        description: i18n.language === 'en'
          ? 'Gentle, sensitive, and artistic. You value harmony and live in the present.'
          : 'Nazik, hassas ve sanatsal. Uyumu değerli bulur ve anı yaşarsınız.'
      },
      'INFP': {
        title: i18n.language === 'en' ? 'The Healer' : 'İyileştirici',
        description: i18n.language === 'en'
          ? 'Idealistic, empathetic, and authentic. You see potential for growth in everyone.'
          : 'İdealist, empatik ve otantik. Herkeste büyüme potansiyeli görürsünüz.'
      },
      'INTP': {
        title: i18n.language === 'en' ? 'The Thinker' : 'Düşünür',
        description: i18n.language === 'en'
          ? 'Logical, conceptual, and curious. You enjoy abstract thinking and problem-solving.'
          : 'Mantıklı, kavramsal ve meraklı. Soyut düşünce ve problem çözmekten keyif alırsınız.'
      },
      'ESTP': {
        title: i18n.language === 'en' ? 'The Dynamo' : 'Dinamo',
        description: i18n.language === 'en'
          ? 'Energetic, practical, and spontaneous. You thrive on action and new experiences.'
          : 'Enerjik, pratik ve kendiliğinden. Eylem ve yeni deneyimlerle gelişirsiniz.'
      },
      'ESFP': {
        title: i18n.language === 'en' ? 'The Performer' : 'Sanatçı',
        description: i18n.language === 'en'
          ? 'Outgoing, friendly, and spontaneous. You bring joy to others and enjoy life\'s pleasures.'
          : 'Dışa dönük, arkadaş canlısı ve kendiliğinden. Başkalarına neşe getirirsiniz ve hayatın zevklerinden keyif alırsınız.'
      },
      'ENFP': {
        title: i18n.language === 'en' ? 'The Champion' : 'Şampiyon',
        description: i18n.language === 'en'
          ? 'Enthusiastic, creative, and people-oriented. You see possibilities everywhere.'
          : 'Coşkulu, yaratıcı ve insan odaklı. Her yerde olasılıklar görürsünüz.'
      },
      'ENTP': {
        title: i18n.language === 'en' ? 'The Innovator' : 'Yenilikçi',
        description: i18n.language === 'en'
          ? 'Inventive, curious, and versatile. You love mental challenges and exploring ideas.'
          : 'Yaratıcı, meraklı ve çok yönlü. Zihinsel mücadelelerden ve fikirleri keşfetmekten hoşlanırsınız.'
      },
      'ESTJ': {
        title: i18n.language === 'en' ? 'The Director' : 'Yönetici',
        description: i18n.language === 'en'
          ? 'Organized, practical, and decisive. You value efficiency and maintaining order.'
          : 'Düzenli, pratik ve kararlı. Verimlilik ve düzeni korumaya değer verirsiniz.'
      },
      'ESFJ': {
        title: i18n.language === 'en' ? 'The Caregiver' : 'Bakıcı',
        description: i18n.language === 'en'
          ? 'Warm, conscientious, and cooperative. You prioritize harmony and meeting others\' needs.'
          : 'Sıcak, vicdanlı ve işbirlikçi. Uyumu ve başkalarının ihtiyaçlarını karşılamayı önceliklendirirsiniz.'
      },
      'ENFJ': {
        title: i18n.language === 'en' ? 'The Teacher' : 'Öğretmen',
        description: i18n.language === 'en'
          ? 'Charismatic, empathetic, and inspiring. You help others develop and reach their potential.'
          : 'Karizmatik, empatik ve ilham verici. Başkalarının gelişmelerine ve potansiyellerine ulaşmalarına yardımcı olursunuz.'
      },
      'ENTJ': {
        title: i18n.language === 'en' ? 'The Commander' : 'Komutan',
        description: i18n.language === 'en'
          ? 'Strategic, logical, and efficient. You naturally take charge and drive for results.'
          : 'Stratejik, mantıklı ve verimli. Doğal olarak sorumluluk alır ve sonuçlar için çaba gösterirsiniz.'
      }
    };
    
    const typeInfo = typeDescriptions[personalityType] || {
      title: personalityType,
      description: i18n.language === 'en' ? 'Your unique personality type' : 'Benzersiz kişilik tipiniz'
    };
    
    return (
      <div className="results-container">
        <h2 className="results-title">{i18n.language === 'en' ? 'Your Personality Profile' : 'Kişilik Profiliniz'}</h2>
        
        <div className="personality-type">
          <div className="type-code">{personalityType}</div>
          <h3 className="type-title">{typeInfo.title}</h3>
          <p className="type-description">{typeInfo.description}</p>
        </div>
        
        <div className="trait-bars">
          <h3>{i18n.language === 'en' ? 'Your Key Traits' : 'Temel Özellikleriniz'}</h3>
          
          <div className="trait-bar">
            <div className="trait-labels">
              <span>{i18n.language === 'en' ? 'Introversion' : 'İçe Dönüklük'}</span>
              <span>{i18n.language === 'en' ? 'Extroversion' : 'Dışa Dönüklük'}</span>
            </div>
            <div className="bar-container">
              <div className="bar-fill" style={{ width: `${traits.extroversion}%` }}></div>
            </div>
          </div>
          
          <div className="trait-bar">
            <div className="trait-labels">
              <span>{i18n.language === 'en' ? 'Sensing' : 'Algılayan'}</span>
              <span>{i18n.language === 'en' ? 'Intuition' : 'Sezgisel'}</span>
            </div>
            <div className="bar-container">
              <div className="bar-fill" style={{ width: `${traits.intuition}%` }}></div>
            </div>
          </div>
          
          <div className="trait-bar">
            <div className="trait-labels">
              <span>{i18n.language === 'en' ? 'Feeling' : 'Hisseden'}</span>
              <span>{i18n.language === 'en' ? 'Thinking' : 'Düşünen'}</span>
            </div>
            <div className="bar-container">
              <div className="bar-fill" style={{ width: `${traits.thinking}%` }}></div>
            </div>
          </div>
          
          <div className="trait-bar">
            <div className="trait-labels">
              <span>{i18n.language === 'en' ? 'Perceiving' : 'Algılayıcı'}</span>
              <span>{i18n.language === 'en' ? 'Judging' : 'Yargılayıcı'}</span>
            </div>
            <div className="bar-container">
              <div className="bar-fill" style={{ width: `${traits.judging}%` }}></div>
            </div>
          </div>
          
          <div className="trait-bar">
            <div className="trait-labels">
              <span>{i18n.language === 'en' ? 'Growth-Focused' : 'Gelişim Odaklı'}</span>
              <span>{i18n.language === 'en' ? 'Stability-Focused' : 'İstikrar Odaklı'}</span>
            </div>
            <div className="bar-container">
              <div className="bar-fill" style={{ width: `${traits.stability}%` }}></div>
            </div>
          </div>
        </div>
        
        <div className="action-buttons">
          <button className="action-button home-button" onClick={() => navigate('/')}>
            {t('returnHome')}
          </button>
        </div>
      </div>
    );
  };

  if (completed) {
    return (
      <div className="quick-test-container">
        <div className="test-completed">
          <h1>{t('testCompleted')}</h1>
          <p>{t('thankYou')}</p>
          {processing ? (
            <div className="processing-indicator">
              <div className="spinner"></div>
              <p>{t('processing')}</p>
            </div>
          ) : (
            renderResults()
          )}
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  return (
    <div className="quick-test-container">
      <div className="test-header">
        <h1>{t('quickTestTitle')}</h1>
        <p className="test-description">{t('quickTestWelcome')}</p>
      </div>

      <div className="progress-indicator">
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }}></div>
        </div>
        <div className="question-counter">
          {t('questionOf').replace('{current}', String(currentQuestionIndex + 1)).replace('{total}', String(questions.length))}
        </div>
      </div>

      <div className="question-container">
        <h2 className="question-text">{t(currentQuestion.key)}</h2>
        
        {currentQuestion.type === 'likert' && renderLikertQuestion(currentQuestion.key)}
        {currentQuestion.type === 'choice' && currentQuestion.options && 
          renderChoiceQuestion(currentQuestion.key, currentQuestion.options)}
        
        {showError && (
          <div className="error-message">
            {t('selectAnswer')}
          </div>
        )}
      </div>

      <div className="navigation-buttons">
        <button 
          className={`nav-button prev ${currentQuestionIndex === 0 ? 'disabled' : ''}`}
          onClick={handlePrevious}
          disabled={currentQuestionIndex === 0}
        >
          {t('previousQuestion')}
        </button>
        <button 
          className="nav-button next"
          onClick={handleNext}
        >
          {currentQuestionIndex === questions.length - 1 ? t('submitAnswers') : t('nextQuestion')}
        </button>
      </div>
    </div>
  );
};

export default QuickTest; 